import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { ChartKey, VCEBlastResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, CONFINEMENT_OPTIONS, LIVE_PLOT_CARD_MIN_HEIGHT, LIVE_PLOT_SIZE_BY_KEY, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid, RichSummaryReport } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'VCE Blast',
          mass: '100',
          LHV: '50',
          mf: '0.59',
          R: '50',
          Confinement: 'UnConfined',
          V: '60*60*15',
          A: '10*10'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, VCEBlastResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, VCEBlastResultState>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });
  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, VCEBlastResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[PRINT_CHART_ASPECT[key]],
    errorMessage: 'Unable to prepare VCE blast charts for printing. Please wait for the charts to finish rendering and try again.',
    enableScreenshot: true
  });

  useSavedCalculationLoader<FormValues, VCEBlastResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Calculation title', placeholder: 'VCE Blast Case' },
    { kind: 'input', name: 'mass', label: 'Vaporized Mass', unit: 'kg', helpText: 'Fuel mass involved in blast', placeholder: '100' },
    { kind: 'input', name: 'LHV', label: 'Low Heat Value', unit: 'MJ/kg', helpText: 'Fuel lower heating value', placeholder: '45' },
    { kind: 'input', name: 'mf', label: 'Mach Factor', unit: '', helpText: 'Blast Mach factor', placeholder: '1.0' },
    { kind: 'input', name: 'R', label: 'Standoff Distance', unit: 'ft', helpText: 'Distance from blast center', placeholder: '50' },
    { kind: 'dropdown', name: 'Confinement', label: 'Confinement', options: CONFINEMENT_OPTIONS, helpText: 'Select Confined or UnConfined' },
    {
      kind: 'input',
      name: 'V',
      label: 'Vessel Volume',
      unit: 'ft^3',
      helpText: 'Total vessel volume',
      placeholder: '1000',
      show: values.Confinement === 'Confined'
    },
    {
      kind: 'input',
      name: 'A',
      label: 'Vessel Surface Area',
      unit: 'ft^2',
      helpText: 'Total vessel surface area',
      placeholder: '600',
      show: values.Confinement === 'Confined'
    }
  ]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Vapor cloud explosion loads</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1 min-w-0 overflow-hidden">
            {analysisResult?.summary ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">{analysisResult ? 'Saved result does not include a summary. Re-run the analysis to generate it.' : 'No analysis results yet.'}</div>}
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData && (
              <div className="analysis-app__charts-grid grid grid-cols-1 xl:grid-cols-2 items-stretch gap-3 m-2 sm:m-4 print:gap-1">
                {Object.entries(analysisResult.plotData)
                  .filter(([, plot]) => !!plot)
                  .map(([key, plot]) => {
                    const chartKey = key as ChartKey;

                    return (
                      <div key={key} className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                          {...LIVE_PLOT_SIZE_BY_KEY[chartKey]}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        <div className="print:hidden hidden lg:block"></div>
      </div>

      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape box-border bg-white p-[1mm]">
            <div className="print-page-content hidden print:grid h-full min-h-0 grid-cols-[24%_76%] border border-black print:bg-white print:text-black">
              <div className="flex  flex-col h-full min-h-0">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'VCE Blast'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[9.5px] leading-[1.18] text-black" />
                  </div>
                </div>
                <div className="grid grid-cols-2 mt-auto p-1 text-[9px] border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-blue-600 hover:underline">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 overflow-hidden">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} fitPage />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
